// src/components/AttendanceRecord.js

import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Select,
  DatePicker,
  Table,
  Button,
  Space,
  Spin,
  Tag,
  Row,
  Col,
  Divider,
  message,
} from "antd";

import {
  UserOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";
import dayjs from "dayjs";

import api from "../../api/api";

const { Title, Text } = Typography;
const { Option } = Select;

const AttendanceRecord = () => {
  // ===========================
  // STATES
  // ===========================

  const [gradeClasses, setGradeClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);

  const [students, setStudents] = useState([]);

  const [attendance, setAttendance] = useState({});

  const [attendanceDate, setAttendanceDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const rowsPerPage = 4;

  // ===========================
  // LOAD GRADE CLASSES
  // ===========================

  useEffect(() => {
    const fetchGradeClasses = async () => {
      try {
        const { data } = await api.get(
          "/api/users/teacher-subjects/"
        );

        setGradeClasses(data.grade_classes || []);
      } catch (err) {
        console.error(err);

        Swal.fire(
          "Error",
          "Unable to load assigned grade classes",
          "error"
        );
      }
    };

    fetchGradeClasses();
  }, []);

  // ===========================
  // LOAD STUDENTS
  // ===========================

  useEffect(() => {
    if (!selectedClass) return;

    const fetchStudents = async () => {
      setLoading(true);

      try {
        const { data } = await api.get(
          `/students-by-grade-class/?grade_class=${selectedClass.id}`
        );

        setStudents(data || []);

        setAttendance(
          Object.fromEntries(
            data.map((student) => [student.id, "present"])
          )
        );

        setPage(1);
      } catch (err) {
        console.error(err);

        Swal.fire(
          "Error",
          "Unable to load students",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [selectedClass]);

  // ===========================
  // CHANGE ATTENDANCE STATUS
  // ===========================

  const handleStatusChange = (studentId, value) => {
    setAttendance((prev) => ({
      ...prev,
      [studentId]: value,
    }));
  };

  // ===========================
  // SUBMIT ATTENDANCE
  // ===========================

  const handleSubmit = async () => {
    if (!selectedClass) {
      Swal.fire(
        "Warning",
        "Please select a grade class",
        "warning"
      );
      return;
    }

    if (!attendanceDate) {
      Swal.fire(
        "Warning",
        "Please select a date for attendance",
        "warning"
      );
      return;
    }

    const payload = Object.entries(attendance).map(
      ([studentId, status]) => ({
        student: parseInt(studentId, 10),
        grade_class: selectedClass.id,
        status,
        date: attendanceDate,
      })
    );

    if (payload.length === 0) {
      Swal.fire(
        "Warning",
        "No students selected for attendance",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      await api.post(
        "/student-attendance/bulk-create/",
        payload
      );

      Swal.fire(
        "Success",
        "Attendance recorded successfully!",
        "success"
      );

      setAttendance(
        Object.fromEntries(
          students.map((student) => [
            student.id,
            "present",
          ])
        )
      );
    } catch (err) {
      console.error(
        err.response?.data || err.message
      );

      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        JSON.stringify(err.response?.data) ||
        err.message;

      Swal.fire(
        "Error",
        `Unable to save attendance: ${errorMessage}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ===========================
  // PAGINATION
  // ===========================

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
  };

  const paginatedStudents =
    students.length > rowsPerPage
      ? students.slice(
          (page - 1) * rowsPerPage,
          (page - 1) * rowsPerPage + rowsPerPage
        )
      : students;

  // ===========================
  // TABLE COLUMNS
  // ===========================

  const columns = [
    {
      title: "Student",
      dataIndex: "full_name",
      key: "student",
      render: (_, record) =>
        record.full_name || record.username,
    },
    {
      title: "Attendance Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          value={attendance[record.id] || "present"}
          onChange={(value) =>
            handleStatusChange(record.id, value)
          }
        >
          <Option value="present">Present</Option>
          <Option value="absent">Absent</Option>
          <Option value="late">Late</Option>
          <Option value="excuse">Excused</Option>
          <Option value="drop">Dropped</Option>
        </Select>
      ),
    },
  ];

  // ===========================
  // PART 2 CONTINUES BELOW
  // ===========================

    // ===========================
  // PROFESSIONAL ANT DESIGN UI
  // ===========================

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: 24,
      }}
    >
      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          boxShadow: "0 10px 30px rgba(0,0,0,.08)",
        }}
      >
        {/* ================= HEADER ================= */}

        <Row
          justify="space-between"
          align="middle"
          gutter={[16, 16]}
        >
          <Col xs={24} md={18}>
            <Space align="start">
              <CheckCircleOutlined
                style={{
                  fontSize: 34,
                  color: "#1677ff",
                  marginTop: 5,
                }}
              />

              <div>
                <Title
                  level={3}
                  style={{
                    margin: 0,
                  }}
                >
                  Student Attendance
                </Title>

                <Text type="secondary">
                  Record daily attendance for students in the
                  selected grade class.
                </Text>
              </div>
            </Space>
          </Col>

          <Col>
            <Tag
              color="blue"
              style={{
                fontSize: 14,
                padding: "6px 12px",
                borderRadius: 20,
              }}
            >
              {students.length} Students
            </Tag>
          </Col>
        </Row>

        <Divider />

        {/* ================= FILTERS ================= */}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={14}>
            <Text strong>
              Grade Class
            </Text>

            <Select
              showSearch
              allowClear
              size="large"
              placeholder="Select Grade Class"
              value={selectedClass?.id}
              style={{
                width: "100%",
                marginTop: 8,
              }}
              disabled={gradeClasses.length === 0}
              optionFilterProp="children"
              onChange={(value) => {
                const cls = gradeClasses.find(
                  (item) => item.id === value
                );

                setSelectedClass(cls || null);
              }}
              filterOption={(input, option) =>
                option?.children
                  ?.toLowerCase()
                  ?.includes(input.toLowerCase())
              }
            >
              {gradeClasses.map((grade) => (
                <Option
                  key={grade.id}
                  value={grade.id}
                >
                  {grade.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={10}>
            <Text strong>
              Attendance Date
            </Text>

            <DatePicker
              size="large"
              style={{
                width: "100%",
                marginTop: 8,
              }}
              value={dayjs(attendanceDate)}
              format="YYYY-MM-DD"
              onChange={(date) =>
                setAttendanceDate(
                  date
                    ? date.format("YYYY-MM-DD")
                    : ""
                )
              }
            />
          </Col>
        </Row>

        {/* ================= LOADING ================= */}

        {loading ? (
          <div
            style={{
              padding: 70,
              textAlign: "center",
            }}
          >
            <Spin size="large" />

            <div
              style={{
                marginTop: 20,
              }}
            >
              <Text type="secondary">
                Loading students...
              </Text>
            </div>
          </div>
        ) : (
          <>
            {/* ================= TABLE ================= */}

            {students.length > 0 && (
              <div
                style={{
                  marginTop: 30,
                }}
              >
                <Table
                  bordered
                  rowKey="id"
                  columns={columns}
                  dataSource={paginatedStudents}
                  pagination={{
                    current: page,
                    pageSize: rowsPerPage,
                    total: students.length,
                    showSizeChanger: false,
                    onChange: (current) =>
                      setPage(current),
                  }}
                  scroll={{
                    y:
                      students.length > 6
                        ? 400
                        : undefined,
                  }}
                />
              </div>
            )}

            {/* ================= EMPTY ================= */}

            {!loading &&
              selectedClass &&
              students.length === 0 && (
                <Card
                  style={{
                    marginTop: 25,
                    textAlign: "center",
                    background: "#fafafa",
                  }}
                >
                  <Space direction="vertical">
                    <UserOutlined
                      style={{
                        fontSize: 42,
                        color: "#bfbfbf",
                      }}
                    />

                    <Title level={5}>
                      No Students Found
                    </Title>

                    <Text type="secondary">
                      This grade class has no registered
                      students.
                    </Text>
                  </Space>
                </Card>
              )}

            {/* ================= FOOTER ================= */}

            {students.length > 0 && (
              <>
                <Divider />

                <Row justify="end">
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    loading={loading}
                    onClick={handleSubmit}
                    style={{
                      minWidth: 220,
                      height: 46,
                      borderRadius: 8,
                      fontWeight: 600,
                    }}
                  >
                    Submit Attendance
                  </Button>
                </Row>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default AttendanceRecord;